import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import * as XLSX from "xlsx";

const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getToday = () => formatDate(new Date());

const MerchantTransferTable = ({ refreshKey }) => {
    const mobile_number = useSelector(
        (state) => state.auth.employeeDetails?.mobile_number
    );

    const mid = useSelector(
        (s) => s.auth.employeeDetails?.username
    );

    const { callApi } = useApi("/ledger/retailer", "GET");
    const { callApi: searchApi } = useApi("/ledger/search-bzc", "GET");
    const { callApi: hierarchy } = useApi("/hierarchy/root", "GET");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [downloading, setDownloading] = useState(false);

    // ✅ DATE STATES
    const [fromDate, setFromDate] = useState(getToday());
    const [toDate, setToDate] = useState(getToday());

    // 🔍 SEARCH STATES
    const [searchText, setSearchText] = useState("");
    const [merchants, setMerchants] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingMerchants, setLoadingMerchants] = useState(false);

    // ================= DATE HANDLERS =================
    const handleFromDateChange = (e) => {
        const val = e.target.value;
        setFromDate(val);
        setToDate(val); // 🔥 always sync
    };

    const handleToDateChange = (e) => {
        const val = e.target.value;
        if (val < fromDate) {
            setToDate(fromDate);
        } else {
            setToDate(val);
        }
    };

    // ================= FETCH MERCHANTS =================
    const fetchMerchants = async () => {
        if (!mobile_number) return;

        setLoadingMerchants(true);

        try {
            const res = await hierarchy(
                null,
                `?mobile_number=${mobile_number}`
            );

            const merchantList =
                res?.self?.children?.filter((c) => c.role === "MERCHANT") || [];

            setMerchants(merchantList);
        } catch (err) {
            setMerchants([]);
        } finally {
            setLoadingMerchants(false);
        }
    };

    // ================= DOWNLOAD EXCEL =================
    const downloadExcel = async () => {
        if (!mid || totalElements === 0) return;

        setDownloading(true);

        try {
            const params = new URLSearchParams();

            if (selectedMerchant) {
                params.append("mid", mid);
                params.append("search", selectedMerchant.mobile || "");
            } else {
                params.append("bid", mid);
                params.append("scope", "self");
                params.append("feature", "BUSINESS_CASH_IN_OUT");
            }

            // ✅ Force page 0, and size to totalElements to get everything
            params.append("page", 0);
            params.append("size", totalElements);
            params.append("fromDate", fromDate);
            params.append("toDate", toDate);

            const res = selectedMerchant
                ? await searchApi(null, `?${params.toString()}`)
                : await callApi(null, `?${params.toString()}`);

            const content = res?.content || [];

            if (content.length > 0) {
                const excelData = content.map((row, index) => ({
                    "Sr. No": index + 1,
                    "Txn ID": row.txnId || "-",
                    "Date & Time": new Date(row.txnDate).toLocaleString("en-IN"),
                    "Narration": row.narration || "-",
                    "Amount": row.txnAmount || 0,
                    "Type": row.txnType || "-",
                    "Closing Balance": row.balance || 0,
                    "Shop Name": row.shopName || "-"
                }));

                const worksheet = XLSX.utils.json_to_sheet(excelData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Transfers");

                const fileName = `Merchant_Transfers_${fromDate}_to_${toDate}.xlsx`;
                XLSX.writeFile(workbook, fileName);
            }
        } catch (err) {
            console.error("Download Error:", err);
            alert("Failed to download data.");
        } finally {
            setDownloading(false);
        }
    };

    // ================= FETCH LEDGER =================
    const fetchLedger = async (targetPage = 0, loadMore = false) => {
        if (!mid) return;

        setLoading(true);

        try {
            const params = new URLSearchParams();
            let res;

            if (selectedMerchant) {
                params.append("mid", mid);
                params.append("search", selectedMerchant.mobile || "");
            } else {
                params.append("bid", mid);
                params.append("scope", "self");
                params.append("feature", "BUSINESS_CASH_IN_OUT");
            }

            params.append("page", targetPage);
            params.append("size", size);
            params.append("fromDate", fromDate);
            params.append("toDate", toDate);

            res = selectedMerchant
                ? await searchApi(null, `?${params.toString()}`)
                : await callApi(null, `?${params.toString()}`);

            const content = res?.content || [];

            setTotalPages(res?.totalPages || 0);
            setTotalElements(res?.totalElements || 0);

            setData((prev) =>
                loadMore ? [...prev, ...content] : content
            );

            setPage(targetPage);
        } catch (err) {
            console.error(err);
            if (!loadMore) setData([]);
        } finally {
            setLoading(false);
        }
    };

    // ================= EFFECTS =================
    useEffect(() => {
        fetchLedger(0, false);
    }, [mid, size, refreshKey, selectedMerchant, fromDate, toDate]);

    // ================= SEARCH HELPERS =================
    const filteredMerchants = merchants.filter((m) =>
        `${m.name} ${m.mobile}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
    );

    const handleSelect = (m) => {
        console.log("Selected Merchant:", m); // 🔍 debug
        setSelectedMerchant(m);
        setSearchText(`${m.name} (${m.mobile})`);
        setShowDropdown(false);
    };

    const clearSearch = () => {
        setSearchText("");
        setSelectedMerchant(null);
        setMerchants([]);
    };

    const handleLoadMore = () => {
        if (page + 1 < totalPages) {
            fetchLedger(page + 1, true);
        }
    };

    const totalDebit = data.reduce(
        (sum, row) =>
            row.txnType === "DEBIT"
                ? sum + Number(row.txnAmount || 0)
                : sum,
        0
    );

    const handleMerchantFocus = () => {
        setShowDropdown(true);
        if (merchants.length === 0) {
            fetchMerchants();
        }
    };

    return (
        <div className="bg-white rounded-xl shadow border p-4 space-y-4">

            {/* HEADER */}
            <div className="flex flex-wrap gap-4 items-end">

                {/* 🔍 SEARCH */}
                <div className="relative w-72">
                    <label className="text-xs text-gray-500 mb-1 block">
                        Search Merchant
                    </label>

                    <input
                        value={searchText}
                        onFocus={() => {
                            handleMerchantFocus();
                        }}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setSelectedMerchant(null);
                        }}
                        placeholder="Search by name / mobile"
                        className="h-11 px-4 border rounded-xl w-full"
                    />

                    {searchText && (
                        <button
                            onClick={clearSearch}
                            className="absolute right-3 top-9 text-red-500"
                        >
                            ✕
                        </button>
                    )}

                    {showDropdown && (
                        <div className="absolute w-full bg-white border shadow rounded-xl mt-2 max-h-52 overflow-auto z-50">
                            {loadingMerchants ? (
                                <div className="p-3 text-sm">Loading...</div>
                            ) : filteredMerchants.length === 0 ? (
                                <div className="p-3 text-sm text-gray-400">
                                    No merchants found
                                </div>
                            ) : (
                                filteredMerchants.map((m) => (
                                    <div
                                        key={m.mid}
                                        onClick={() => handleSelect(m)}
                                        className="px-4 py-3 hover:bg-indigo-50 cursor-pointer"
                                    >
                                        <div className="font-medium">{m.name}</div>
                                        <div className="text-xs text-gray-500">
                                            {m.mobile}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* ROWS */}
                <select
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className="h-11 px-4 border rounded-xl"
                >
                    {[25, 50, 100, 200].map((n) => (
                        <option key={n}>{n}</option>
                    ))}
                </select>

                {/* 📅 DATE */}
                <input
                    type="date"
                    value={fromDate}
                    max={getToday()}
                    onChange={handleFromDateChange}
                    className="h-11 px-3 border rounded-xl"
                />

                <input
                    type="date"
                    value={toDate}
                    max={getToday()}
                    min={fromDate}
                    onChange={handleToDateChange}
                    className="h-11 px-3 border rounded-xl"
                />

                {/* SUMMARY */}
                {/* <div className="ml-auto text-right">
                    <div className="text-red-600 font-bold">
                        ₹{totalDebit.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                        {totalElements} Records
                    </div>
                </div> */}
                {/* SUMMARY & DOWNLOAD */}
                <div className="ml-auto flex items-center gap-4">
                    <div className="text-right">
                        <div className="text-red-600 font-bold">
                            ₹{totalDebit.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">
                            {totalElements} Records
                        </div>
                    </div>
                    
                    <button
                        onClick={downloadExcel}
                        // ✅ Disabled if no records, if loading table, or if currently downloading
                        disabled={totalElements === 0 || loading || downloading}
                        className={`h-11 px-6 rounded-xl font-semibold text-white transition whitespace-nowrap
                            ${totalElements === 0 || loading || downloading 
                                ? "bg-gray-400 cursor-not-allowed" 
                                : "bg-green-600 hover:bg-green-700 shadow-md"
                            }`}
                    >
                        {downloading ? "Downloading..." : "Download Excel"}
                    </button>
                </div>

            </div>

            {/* DATA */}
            {loading ? (
                <div className="text-center py-6">Loading...</div>
            ) : data.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                    No Records
                </div>
            ) : (
                <>
                    <div className="divide-y">
                        {data.map((row, i) => {
                            const isDebit = row.txnType === "DEBIT";

                            return (
                                <div key={i} className={isDebit ? "py-4 flex justify-between" : "py-4 flex justify-between text-green-600 font-bold"}>
                                    <div>
                                        {/* <div className="text-xs text-gray-500">
                                            {isDebit ? "Paid to" : "Received"}
                                        </div> */}
                                        <div className="font-semibold">
                                            {row.narration}
                                        </div>
                                        <div className="text-xs">
                                            {new Date(row.txnDate).toLocaleString("en-IN")}
                                        </div>
                                    </div>

                                    <div className={isDebit ? "text-red-500 font-bold" : "text-green-600 font-bold"}>
                                        ₹{Number(row.txnAmount).toFixed(2)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {page + 1 < totalPages && (
                        <div className="text-center pt-4">
                            <button
                                onClick={handleLoadMore}
                                className="px-6 py-2 bg-black text-white rounded"
                            >
                                Load More
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MerchantTransferTable;